'use client';

import { useRef } from 'react';

import { useMiniGamePageConfig } from '@/app/(football)/[team]/mini-game/_context/context';

import { GameUI } from './GameUI';
import { useBlockStackingGame } from './hooks/useBlockStackingGame';
import type { GameConfig } from './types';
import { DEFAULT_GAME_CONFIG } from './types';

/**
 * Main block stacking game component
 * 3D physics simulation game using Three.js and Cannon.js
 */
export function BlockStackingGame() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const { game } = useMiniGamePageConfig();

  const gameConfig: GameConfig = {
    ...DEFAULT_GAME_CONFIG,
    colors: game.colors,
    background: { color: game.background.color },
  };

  const { score, gameState, startGame, handleGameAction, resetGame } = useBlockStackingGame(canvasRef, gameConfig);

  // Touch-first handler (prevents duplicate events)
  const handleTouchAction = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleGameAction();
  };

  // Mouse-only handler (only for non-touch devices)
  const handleMouseAction = (e: React.MouseEvent) => {
    if ('ontouchstart' in window) return;

    e.preventDefault();
    e.stopPropagation();
    handleGameAction();
  };

  return (
    <div className="relative h-[488px] w-full cursor-pointer overflow-hidden rounded-lg">
      {/* 3D game rendering area */}
      <div
        ref={canvasRef}
        onMouseDown={handleMouseAction}
        onTouchStart={handleTouchAction}
        className="flex h-full w-full touch-none items-center justify-center"
      />

      {/* Game UI overlay */}
      <GameUI score={score} gameState={gameState} onStartGame={startGame} onResetGame={resetGame} />
    </div>
  );
}
